import { useQuery, UseQueryResult } from "react-query";
import { getDrugsLookup } from "services/drugs";
import { LookupItem } from "api/types";
import useDebouncedValue from "hooks/useDebouncedValue";

/**
 * Search-as-you-type sur le catalogue Drugs — débouncé 250 ms, ne fire
 * que si la requête fait au moins 2 caractères (le serveur renverrait
 * `[]` sinon, mais autant éviter le round-trip). Le `queryKey` inclut
 * `q` pour que TanStack Query cache chaque requête : retaper la même
 * chaîne ne refetch pas.
 *
 * Le `select` aplatit l'enveloppe `apiRequest` en `LookupItem[]` direct.
 *
 * `keepPreviousData: true` évite le flicker pendant la frappe — le
 * combobox affiche les anciens résultats jusqu'à ce que les nouveaux
 * arrivent.
 */
export function useDrugsLookupSearch(
  q: string,
  limit: number = 20
): UseQueryResult<LookupItem[]> {
  const debouncedQ = useDebouncedValue(q, 250);
  const trimmed = debouncedQ.trim();

  return useQuery<any, unknown, LookupItem[]>(
    ["drugs-lookup", trimmed, limit],
    () => getDrugsLookup({ q: trimmed, limit }),
    {
      enabled: trimmed.length >= 2,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
      select: (result: any) => (result?.ok ? result.data ?? [] : []),
    }
  );
}

export default useDrugsLookupSearch;
